/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';

import { calculateTimeLeft, checkCampaignStatus } from '../../utils/OfferUtils';
import { shouldUseMockData } from '../../utils/CommonUtils';

import { OnOffContainerProps } from './OnOffContainer.types';

export const OnOffContainerWrapper = (props: any) => {
  const { isAuthorMode, model, mbeData, isPreview, mbeModelData, mbeDynamicWorkflow } = props;
  const { variation } = model;
  const pathParam = useSearchParams();
  const cycleState = pathParam?.get('cycle');

  const [offersDataMock, setoffersDataMock] = useState<any>({});

  const mbeOffersData = mbeDynamicWorkflow ? mbeData?.responseObject?.offers?.mbeOfferById?.offers : mbeModelData;
  const sourceMBEData = !isAuthorMode ? mbeOffersData : offersDataMock;

  const campaignStartDate =
    (mbeDynamicWorkflow ? sourceMBEData?.onCycleStartDate : sourceMBEData?.campaignStartDate) || '';
  const campaignEndDate = (mbeDynamicWorkflow ? sourceMBEData?.onCycleEndDate : sourceMBEData?.campaignEndDate) || '';

  const [utilityState, setUtilityState] = useState<string>(() =>
    checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview)
  );

  useEffect(() => {
    if (!isAuthorMode || !shouldUseMockData(isAuthorMode)) return;

    let isMounted = true;

    import('./__mock__/OnOffContainerMock.mock').then(mockData => {
      if (isMounted) {
        setoffersDataMock(mockData.offersDataMock);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isAuthorMode]);

  useEffect(() => {
    if (isAuthorMode || !campaignStartDate) return;

    const timeLeft = calculateTimeLeft(campaignStartDate, isAuthorMode);
    const hasTimeLeft = timeLeft.days > 0 || timeLeft.hours > 0 || timeLeft.minutes > 0 || timeLeft.seconds > 0;

    if (!hasTimeLeft) {
      setUtilityState(checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview));
      return;
    }

    const updateTime = () => {
      const newTimeLeft = calculateTimeLeft(campaignStartDate, isAuthorMode);

      if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        setUtilityState(checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview));
      }
    };

    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [isAuthorMode, campaignStartDate, campaignEndDate]);

  useEffect(() => {
    if (typeof window === 'undefined' || isPreview !== 'true') return;

    let timeoutId: NodeJS.Timeout;
    let rafId: number;

    function updateMargin() {
      rafId = requestAnimationFrame(() => {
        const previewTextElement = document.querySelector('[data-component-name="o-offers-text"]');
        const searchForm = document.querySelector('[data-component-name="o-shop-searchform"]');

        if (!previewTextElement || !searchForm) return;

        const searchFormChild = searchForm.querySelector('.document_search_form_container_el_ref');
        const previewElementBottom = (previewTextElement as HTMLElement).getBoundingClientRect().bottom;
        const topInPx = searchFormChild
          ? parseFloat(window.getComputedStyle(searchFormChild as HTMLElement).top) || 0
          : 0;

        (searchForm as HTMLElement).style.marginTop = `${previewElementBottom - topInPx}px`;
      });
    }

    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateMargin, 150);
    }

    timeoutId = setTimeout(updateMargin, 500);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [isPreview]);

  if (variation === utilityState || isAuthorMode) {
    return <ResponsiveGrid isInEditor={true} customClassName={clsx(model?.appliedCssClassNames)} {...props} />;
  }

  return null;
};

export const OnOffContainerWrapperConfig = {
  emptyLabel: 'OnOffContainer',
  isEmpty: false,
  resourceType: `mi-aem-offers-spa/components/onoffcontainer`,
};

export const OnOffContainer: FC<OnOffContainerProps> = (props: any) => {
  return (
    <EditableComponent config={OnOffContainerWrapperConfig} {...props}>
      <div data-testid="offers-OnOffContainer" data-component-name="o-offers-OnOffContainer">
        <OnOffContainerWrapper {...props} />
      </div>
    </EditableComponent>
  );
};
